import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";

import SoftBox from "components/SoftBox";
import TeacherProfileLink from "components/TeacherProfileLink";
import TeacherAvatar from "components/TeacherAvatar";
import SoftTypography from "components/SoftTypography";
import Loading from "components/Loading";
import api from "services/api";
import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";

const teachersListUrl = (id) => `${apiurl}Recruiters/teacherslist?Id=${id}`;

const dialogPaperProps = {
  sx: {
    borderRadius: "16px",
    overflow: "hidden",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.14)",
  },
};

function nonEmptyStr(value) {
  return String(value ?? "").trim().length > 0;
}

/** Profile coverage from teacher rows (API does not expose enrollment dates). */
export function computeRecruiterTeacherListStats(teachers) {
  const list = Array.isArray(teachers) ? teachers : [];
  return {
    total: list.length,
    withPhone: list.filter((x) => nonEmptyStr(x?.phone)).length,
    withEmail: list.filter((x) => nonEmptyStr(x?.email)).length,
    withWhatsApp: list.filter((x) => nonEmptyStr(x?.whatsapp)).length,
  };
}

const statTileSx = {
  flex: "1 1 140px",
  minWidth: 0,
  p: 1.5,
  borderRadius: "12px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  background: `linear-gradient(145deg, rgba(102, 126, 234, 0.06) 0%, ${figmaTokens.surface} 55%)`,
};

function RecruiterTeachersDialog({ open, onClose, recruiter, rowsPerPage = 10 }) {
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [page, setPage] = useState(1);

  const token = useMemo(() => localStorage.getItem("tk446655440000"), []);
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const stats = useMemo(() => computeRecruiterTeacherListStats(teachers), [teachers]);

  useEffect(() => {
    if (!open || recruiter?.id == null) return;

    let cancelled = false;
    setPage(1);
    setTeachers([]);
    setError("");
    setLoading(true);

    (async () => {
      try {
        const { data } = await api.get(teachersListUrl(recruiter.id), {
          headers: { ...authHeader },
        });
        if (!cancelled) {
          setTeachers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setTeachers([]);
          setError(t("recruiters.teachers_load_error", "Failed to load teachers for this recruiter."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when dialog opens for recruiter id
  }, [open, recruiter?.id, authHeader]);

  const totalPages = Math.ceil(teachers.length / rowsPerPage);
  const pagedTeachers = teachers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperProps={dialogPaperProps}>
      <DialogTitle sx={{ p: 0 }}>
        <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
        <SoftBox sx={{ px: 2.5, py: 2.25, pr: 7, position: "relative" }}>
          <SoftTypography
            variant="overline"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.14em",
              color: figmaTokens.textMuted,
              fontSize: "0.68rem",
            }}
          >
            {t("recruiters.teachers_modal_kicker", "TEAM")}
          </SoftTypography>
          <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
            {t("recruiters.teachers", "Enseignants")} — {recruiter?.name || "—"}
          </SoftTypography>
          <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
            {t("recruiters.teachers_modal_hint", "Teachers assigned to this recruiter.")}
          </SoftTypography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ mt: 2, alignItems: { xs: "stretch", sm: "stretch" } }}
          >
            <SoftBox sx={statTileSx}>
              <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, fontWeight: 700, display: "block" }}>
                {t("recruiters.stats.enrolled_total", "Teachers enrolled")}
              </SoftTypography>
              <SoftTypography variant="h5" fontWeight={800} sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                {stats.total}
              </SoftTypography>
            </SoftBox>
            <SoftBox sx={statTileSx}>
              <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, fontWeight: 700, display: "block" }}>
                {t("recruiters.stats.with_phone", "With phone")}
              </SoftTypography>
              <SoftTypography variant="h5" fontWeight={800} sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                {stats.withPhone}
              </SoftTypography>
            </SoftBox>
            <SoftBox sx={statTileSx}>
              <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, fontWeight: 700, display: "block" }}>
                {t("recruiters.stats.with_email", "With email")}
              </SoftTypography>
              <SoftTypography variant="h5" fontWeight={800} sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                {stats.withEmail}
              </SoftTypography>
            </SoftBox>
          </Stack>

          {!loading && (
            <SoftTypography variant="caption" sx={{ display: "block", mt: 1.25, color: figmaTokens.textMuted, fontWeight: 600 }}>
              {t("recruiters.teachers_contact_hint", "{{phone}} with phone · {{email}} with email · {{wa}} with WhatsApp")
                .replace("{{phone}}", String(stats.withPhone))
                .replace("{{email}}", String(stats.withEmail))
                .replace("{{wa}}", String(stats.withWhatsApp))}
            </SoftTypography>
          )}

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 10,
              top: 14,
              color: figmaTokens.textSecondary,
              "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </SoftBox>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pb: 2, pt: 1 }}>
        {loading && (
          <SoftBox py={5} display="flex" justifyContent="center">
            <Loading />
          </SoftBox>
        )}
        {error && (
          <SoftBox mb={2}>
            <Alert severity="error" sx={{ borderRadius: "12px" }}>
              {error}
            </Alert>
          </SoftBox>
        )}
        {!loading && !error && (
          <SoftBox>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: `1px solid ${figmaTokens.borderSubtle}`,
                bgcolor: figmaTokens.surface,
                overflow: "hidden",
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 720,
                  ...directoryTableHeadStickyOverridesSx,
                  ...directoryTableBodyCellDefaultsSx,
                }}
                aria-label={t("recruiters.teachers", "Enseignants")}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.reg", "Matricule")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.last", "Nom")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.first", "Prénoms")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.gender", "Sexe")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.degree", "Diplôme")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.phone", "Téléphone")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.whatsapp", "WhatsApp")}</TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("recruiters.teachers.email", "Email")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedTeachers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                          {t("recruiters.teachers.none", "Aucun enseignant trouvé.")}
                        </SoftTypography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedTeachers.map((teacher) => (
                    <TableRow
                      key={teacher.id || teacher.regNumber}
                      hover
                      sx={{
                        "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.85)" },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{teacher.regNumber || "--"}</TableCell>
                      <TableCell>
                        <TeacherProfileLink id={teacher.id} regNumber={teacher.regNumber}>
                          <SoftBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
                            <TeacherAvatar teacher={teacher} size="xs" />
                            <SoftTypography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: figmaTokens.accent,
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {teacher.name || "--"}
                            </SoftTypography>
                          </SoftBox>
                        </TeacherProfileLink>
                      </TableCell>
                      <TableCell>
                        <TeacherProfileLink id={teacher.id} regNumber={teacher.regNumber}>
                          <SoftTypography variant="body2" sx={{ color: figmaTokens.accent, "&:hover": { textDecoration: "underline" } }}>
                            {teacher.preName || "--"}
                          </SoftTypography>
                        </TeacherProfileLink>
                      </TableCell>
                      <TableCell>{teacher.gender || "--"}</TableCell>
                      <TableCell>{teacher.degree || "--"}</TableCell>
                      <TableCell>{teacher.phone || "--"}</TableCell>
                      <TableCell>{teacher.whatsapp || "--"}</TableCell>
                      <TableCell>{teacher.email || "--"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {teachers.length > rowsPerPage && (
              <Stack spacing={2} alignItems="center" sx={{ py: 2.25, borderTop: `1px solid ${figmaTokens.cardHeaderRule}` }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Stack>
            )}
          </SoftBox>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${figmaTokens.cardHeaderRule}`,
          bgcolor: figmaTokens.pageBg,
        }}
      >
        <Button
          variant="contained"
          disableElevation
          onClick={handleClose}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "12px",
            px: 2.75,
            py: 1,
            color: "#fff",
            background: figmaTokens.gradientCSS,
            boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
            "&:hover": {
              filter: "brightness(1.06)",
              boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
            },
          }}
        >
          {t("recruiters.close", "Fermer")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RecruiterTeachersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recruiter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
  rowsPerPage: PropTypes.number,
};

RecruiterTeachersDialog.defaultProps = {
  recruiter: null,
  rowsPerPage: 10,
};

export default RecruiterTeachersDialog;
